import { useEffect, useRef } from 'react';
import { wsService } from './WebSocketService';

/**
 * React hook for WebSocket rooms.
 * Joins `room` while mounted (ref-counted: other components may share it) and
 * listens to the given events FROM THAT ROOM only. The latest handlers are
 * always called, so they do not need to be memoised.
 *
 * @param {string} room - Room to join (e.g., 'admin-restaurant:123')
 * @param {Object<string, Function>} eventHandlers - Map of event names to handler functions
 * @param {object} options - Additional options
 * @param {boolean} options.enabled - Whether to join (default: true)
 * @param {any[]} options.deps - Dependencies that trigger re-subscription
 * @param {Function} [options.onResync] - Events may have been missed (reconnection
 *        without a complete replay): refetch the page's data.
 *
 * @example
 * useWebSocket('admin-restaurant:123', {
 *   'order::new': (data) => {},
 *   'order::update': (data) => {},
 * }, { onResync: reload });
 */
export function useWebSocket(room, eventHandlers = {}, { enabled = true, deps = [], onResync } = {}) {
  const handlersRef = useRef(eventHandlers);
  handlersRef.current = eventHandlers;
  const resyncRef = useRef(onResync);
  resyncRef.current = onResync;
  const events = Object.keys(eventHandlers).sort().join('|');

  useEffect(() => {
    if (!enabled || !room) {
      return undefined;
    }

    const release = wsService.join(room, {
      onResync: (info) => resyncRef.current?.(info),
    });
    const unsubscribers = events
      .split('|')
      .filter(Boolean)
      .map((event) =>
        wsService.on(event, (data, meta) => handlersRef.current[event]?.(data, meta), { room })
      );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, enabled, events, ...deps]);

  return {
    isConnected: wsService.isConnected(room || null),
    emit: (event, data) => wsService.emit(event, data, room || null),
    disconnect: wsService.disconnect.bind(wsService),
  };
}

/**
 * Hook for subscribing to a single WebSocket event (every joined room).
 * Does not join a room by itself.
 *
 * @param {string} event - Event name
 * @param {Function} handler - Event handler function
 * @param {any[]} deps - Dependencies that trigger re-subscription
 */
export function useWebSocketEvent(event, handler, deps = []) {
  useEffect(() => {
    if (!event || !handler) {
      return undefined;
    }

    const unsubscribe = wsService.on(event, handler);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, ...deps]);
}
